import { useMemo, useState } from "react";
import "@fontsource-variable/unbounded";
import type { RankedEntry, SiteProps, Slot } from "../types";
import "../styles/shop.css";

interface StorefrontDefinition {
  id: string;
  label: string;
  unit: string;
  wing: string;
}

interface Storefront extends StorefrontDefinition {
  slot: Slot;
}

const STOREFRONTS: readonly StorefrontDefinition[] = [
  { id: "shop-01", label: "Orange Kiosk", unit: "K-01", wing: "Dead center" },
  { id: "shop-02", label: "Unit 02", unit: "A-02", wing: "North walk" },
  { id: "shop-03", label: "Food Court A", unit: "FC-A", wing: "Upper level" },
  { id: "shop-04", label: "Escalator Left", unit: "A-04", wing: "North walk" },
  { id: "shop-05", label: "Unit 05", unit: "A-05", wing: "North walk" },
  { id: "shop-06", label: "Tiny Corner Shop", unit: "K-06", wing: "Sharp corner" },
  { id: "shop-07", label: "Atrium Prime", unit: "AT-07", wing: "Main atrium" },
  { id: "shop-08", label: "Unit 08", unit: "B-08", wing: "West walk" },
  { id: "shop-09", label: "Food Court B", unit: "FC-B", wing: "Upper level" },
  { id: "shop-10", label: "Unit 10", unit: "B-10", wing: "West walk" },
  { id: "shop-11", label: "Fountain Facing", unit: "K-11", wing: "Wet center" },
  { id: "shop-12", label: "Unit 12", unit: "C-12", wing: "East walk" },
  { id: "shop-13", label: "Cinema Lobby", unit: "CIN-13", wing: "Upper level" },
  { id: "shop-14", label: "Unit 14", unit: "C-14", wing: "East walk" },
  { id: "shop-15", label: "Unit 15", unit: "C-15", wing: "East walk" },
  { id: "shop-16", label: "Loading Bay Chic", unit: "D-16", wing: "Freight side" },
  { id: "shop-17", label: "Unit 17", unit: "D-17", wing: "Cold end" },
  { id: "shop-18", label: "Last Shop Standing", unit: "Z-18", wing: "Beyond retail" },
] as const;

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function formatMoney(amountCents: number): string {
  return money.format(amountCents / 100);
}

function rankedEntries(slot: Slot): RankedEntry[] {
  return [...slot.entries].sort(
    (left, right) => left.rank - right.rank || right.amountCents - left.amountCents,
  );
}

function topEntry(slot: Slot): RankedEntry | undefined {
  return rankedEntries(slot)[0];
}

function displayHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function safeWebsiteUrl(url: string): string | undefined {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.href : undefined;
  } catch {
    return undefined;
  }
}

export default function ShopSite({ slots, state, onClaim }: SiteProps) {
  const storefronts = useMemo<Storefront[]>(() => {
    const slotsById = new Map(slots.map((slot) => [slot.id, slot]));

    return STOREFRONTS.map((definition) => ({
      ...definition,
      slot: slotsById.get(definition.id) ?? {
        id: definition.id,
        label: definition.label,
        entries: [],
      },
    }));
  }, [slots]);

  const firstOccupiedId = storefronts.find(({ slot }) => slot.entries.length > 0)?.id;
  const [selectedId, setSelectedId] = useState(firstOccupiedId ?? storefronts[0].id);
  const selectedStore =
    storefronts.find((storefront) => storefront.id === selectedId) ?? storefronts[0];
  const selectedEntries = rankedEntries(selectedStore.slot);
  const selectedWinner = selectedEntries[0];
  const occupiedCount = storefronts.filter(({ slot }) => slot.entries.length > 0).length;

  function selectStorefront(id: string): void {
    setSelectedId(id);

    if (window.matchMedia("(max-width: 760px)").matches) {
      window.requestAnimationFrame(() => {
        document.getElementById("shop-lease-detail")?.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "start",
        });
      });
    }
  }

  return (
    <main className="shop-site">
      <header className="shop-header">
        <div className="shop-header__inner">
          <a className="shop-wordmark" href="/" aria-label="Outbid Shop home">
            OUTBID<span>.SHOP</span>
          </a>

          <div className="shop-header__status" aria-label="Marketplace status">
            <span aria-hidden="true" />
            {state.stripeEnabled ? "BIDDING OPEN" : "VIEWING ONLY"}
          </div>
        </div>
      </header>

      <div className="shop-shell">
        <section className="shop-hero" aria-labelledby="shop-title">
          <p className="shop-kicker">THE INTERNET STRIP MALL</p>
          <h1 id="shop-title">Put your website in the window.</h1>
          <p className="shop-intro">
            Choose a storefront. The highest paid total gets the sign; every paid bidder stays in
            its public directory.
          </p>
          <button
            className="shop-hero-claim"
            type="button"
            disabled={!state.stripeEnabled}
            onClick={() => onClaim(selectedStore.slot)}
          >
            <span>{state.stripeEnabled ? `Enter ${selectedStore.label}` : "Payments opening soon"}</span>
            <strong>{state.stripeEnabled ? `from ${formatMoney(state.minimumBidCents)} →` : "VIEW ONLY"}</strong>
          </button>
        </section>

        <div className="shop-proof" aria-label="Marketplace facts">
          <span>{occupiedCount > 0 ? <><strong>{occupiedCount}</strong> of {storefronts.length} storefronts claimed</> : <><strong>{storefronts.length}</strong> launch storefronts</>}</span>
          <span>
            Entry from <strong>{formatMoney(state.minimumBidCents)}</strong>
          </span>
          <span>{occupiedCount > 0 ? "Outbid means #2, not deleted" : "First paid listing takes #1"}</span>
        </div>

        <section className="shop-marketplace" aria-labelledby="shop-directory-title">
          <div className="shop-directory">
            <div className="shop-directory__heading">
              <div>
                <p>AVAILABLE NOW</p>
                <h2 id="shop-directory-title">Pick a storefront</h2>
              </div>
              <span>Select any unit to see its ranking.</span>
            </div>

            <div className="shop-storefront-grid">
              {storefronts.map((storefront) => {
                const winner = topEntry(storefront.slot);
                const isSelected = storefront.id === selectedStore.id;

                return (
                  <button
                    className={`shop-unit ${winner ? "shop-unit--occupied" : "shop-unit--vacant"} ${
                      isSelected ? "shop-unit--selected" : ""
                    }`}
                    key={storefront.id}
                    type="button"
                    aria-pressed={isSelected}
                    aria-controls="shop-lease-detail"
                    onClick={() => selectStorefront(storefront.id)}
                  >
                    <span className="shop-unit__topline">
                      <b>{storefront.unit}</b>
                      <small>{winner ? "CLAIMED" : "OPEN"}</small>
                    </span>
                    <strong className="shop-unit__name">{storefront.label}</strong>
                    <span className="shop-unit__wing">{storefront.wing}</span>
                    <span className="shop-unit__bid">
                      {winner ? (
                        <>
                          <span>#1 {winner.name}</span>
                          <b>{formatMoney(winner.amountCents)}</b>
                        </>
                      ) : (
                        <>
                          <span>Be first</span>
                          <b>{formatMoney(state.minimumBidCents)}</b>
                        </>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <aside className="shop-detail" id="shop-lease-detail" aria-live="polite">
            <div className="shop-detail__unit">
              <span>SELECTED SHOP</span>
              <strong>{selectedStore.unit}</strong>
            </div>

            <p className="shop-detail__location">
              {selectedStore.label} · {selectedStore.wing}
            </p>

            {selectedWinner ? (
              <div className="shop-detail__leader">
                <span>CURRENT #1</span>
                <h2>{selectedWinner.name}</h2>
                <p>{selectedWinner.tagline}</p>
                <strong>{formatMoney(selectedWinner.amountCents)} total</strong>
              </div>
            ) : (
              <div className="shop-detail__leader shop-detail__leader--empty">
                <span>WINDOW AVAILABLE</span>
                <h2>Be first on the sign.</h2>
                <p>Your domain will appear here.</p>
              </div>
            )}

            <button
              className="shop-claim-button"
              type="button"
              disabled={!state.stripeEnabled}
              onClick={() => onClaim(selectedStore.slot)}
            >
              <span>{state.stripeEnabled ? "ENTER THIS RANKING" : "PAYMENTS OPENING SOON"}</span>
              <strong>{state.stripeEnabled ? `FROM ${formatMoney(state.minimumBidCents)} →` : "VIEW ONLY"}</strong>
            </button>

            <p className="shop-detail__reassurance">
              Pay once. Higher totals move up; paid listings remain visible.
            </p>

            <div className="shop-ranking">
              <div className="shop-ranking__header">
                <strong>Current ranking</strong>
                <span>{selectedEntries.length} listed</span>
              </div>

              {selectedEntries.length > 0 ? (
                <ol className="shop-ranking__list">
                  {selectedEntries.map((entry) => {
                    const websiteUrl = safeWebsiteUrl(entry.url);

                    return (
                      <li key={entry.id}>
                        <span className="shop-ranking__number">#{entry.rank}</span>
                        <span className="shop-ranking__identity">
                          <strong>{entry.name}</strong>
                          {websiteUrl ? (
                            <a href={websiteUrl} target="_blank" rel="noreferrer">
                              {displayHost(entry.url)} ↗
                            </a>
                          ) : (
                            <span>{displayHost(entry.url)}</span>
                          )}
                        </span>
                        <strong className="shop-ranking__amount">
                          {formatMoney(entry.amountCents)}
                        </strong>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <p className="shop-ranking__empty">No bids yet. The first paid listing takes #1.</p>
              )}
            </div>
          </aside>
        </section>
      </div>

      <footer className="shop-footer">
        <div>
          <strong>OUTBID.SHOP</strong>
          <span>Highest total owns the sign.</span>
        </div>
        <nav aria-label="Legal">
          <a href="/rules">Rules</a>
          <a href="/terms">Terms</a>
          <a href="/privacy">Privacy</a>
        </nav>
      </footer>
    </main>
  );
}
